import * as i1 from '@upr-web-primer/app-utils';
import { param, paramBool } from '@upr-web-primer/app-utils';
import * as i2 from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import * as i3 from '@upr-web-primer/locale';
import * as i4 from '@upr-web-primer/storage';
import { WINDOW } from '@upr-web-primer/window';
import { isEmpty, get } from 'lodash-es';
import { from, firstValueFrom, throwError } from 'rxjs';
import { shareReplay, tap, map, delay, distinctUntilChanged, retry, filter, catchError } from 'rxjs/operators';
const TCM_REPLACED_DEFAULTS = {
  tridion: {
    app_directory: param('$tridion.appdirectory$') || param('$tridion.app_directory$'),
    config_enabled: paramBool('$tridion.config_enabled$'),
    config_url: param('$tridion.configUrl$') || param('$tridion.config_url$'),
    dynamic_path_enabled: paramBool('$tridion.dynamicpath.enabled$') || paramBool('$tridion.dynamic_path_enabled$'),
    publication: param('$tridion.site$') || param('$tridion.publication$')
  },
  content_api: {
    enabled: paramBool('$contentapi.enabled$'),
    path: param('$contentapi.path$'),
    data_path: param('$contentapi.datapath$'),
    pages: param('$contentapi.pages$')
  }
};
const TCM_ENV_DEFAULTS = {
  tridion: {
    app_directory: '',
    config_enabled: false,
    config_url: '/k2/en/us/api/getcontent/?contentIds=upr-gds-microsite-configurator',
    dynamic_path_enabled: false,
    publication: 'k2'
  },
  content_api: {
    enabled: false,
    path: 'api/content',
    data_path: '/tridiondata',
    pages: '/places-to-stay/|/[a-z]{2}/[a-z]{2}/index.html|/layout/|/interact/|/tickets-packages/|/things-to-do/'
  }
};
const ContentUrls = {
  TRIDION: {
    CONTENT_ID_PATH: 'api/getcontent/?contentIds='
  }
};
class TcmService {
  constructor(appEnvSrv, httpClient, localeService, sessionStorage, tridionBaseUrl, window) {
    this.appEnvSrv = appEnvSrv;
    this.httpClient = httpClient;
    this.localeService = localeService;
    this.sessionStorage = sessionStorage;
    this.tridionBaseUrl = tridionBaseUrl;
    this.window = window;
    this.GDS_MICROSITE_CONFIG_KEY = 'GDS-MICROSITE-CONFIG';
    this.contentApiEnabled = false;
    this.appEnvSrv.addDefaultValues(TCM_ENV_DEFAULTS, TCM_REPLACED_DEFAULTS);
    this.tridionEnvConfig = this.appEnvSrv.getEnvVariables('tridion');
    this.contentEnvConfig = this.appEnvSrv.getEnvVariables('content_api');
    const contentApiEnabled = this.contentEnvConfig.enabled;
    const contentApiPages = this.contentEnvConfig.pages;
    const contentApiHost = this.contentEnvConfig.data_path;
    if (this.window.location.hostname === contentApiHost && contentApiEnabled && !!contentApiPages) {
      this.contentApiEnabled = true;
      this.contentApiPagesRegExp = new RegExp(contentApiPages, 'gi');
      this.contentApiPath = this.contentEnvConfig.path;
    }
    if (this.tridionEnvConfig.config_enabled) {
      const cachedConfig = this.sessionStorage.getItem(this.GDS_MICROSITE_CONFIG_KEY, true);
      this.tridionConfig$ = cachedConfig ? from([cachedConfig]).pipe(shareReplay(1)) : from(this.request(this.tridionEnvConfig.config_url)).pipe(tap(config => {
        if (!isEmpty(config)) {
          this.sessionStorage.setItem(this.GDS_MICROSITE_CONFIG_KEY, config);
        }
      }), shareReplay(1));
      this.siteConfig$ = this.tridionConfig$.pipe(map(tridionConfig => {
        const sites = get(tridionConfig, '0.sites', []);
        return this.getSiteConfig(sites);
      }));
    }
  }
  request(url, currentRetry) {
    const delayTime = (currentRetry || 0) > 0 ? 500 : 0;
    url = url.indexOf('/') === 0 ? `${this.tridionBaseUrl}${url}` : `${this.tridionBaseUrl}/${url}`;
    if (this.contentApiEnabled && url.match(this.contentApiPagesRegExp) !== null) {
      url = url.replace(this.tridionBaseUrl, this.contentApiPath);
    }
    return firstValueFrom(this.httpClient.get(url).pipe(delay(delayTime), distinctUntilChanged(), retry(3), filter(Boolean), catchError(() => throwError(() => new Error(`Couldn't retrieve ${url}`)))));
  }
  requestContentById(contentId, publication = this.tridionEnvConfig.publication) {
    const locale = this.localeService.getFromStorage();
    const language = locale?.language;
    const country = locale?.country;
    return this.request(`/${publication}/${language}/${country}/${ContentUrls.TRIDION.CONTENT_ID_PATH}${contentId}`);
  }
  getSiteConfig(sites) {
    if (!sites) return;
    const paths = this.window.location.pathname.split('/');
    const path = paths.length > 1 ? paths[1] : '';
    const hostname = this.window.location.hostname;
    let sameDomain = false;
    let matchedConfig = sites.find(siteConfig => {
      const siteSegments = siteConfig.site?.split('/') || [];
      const siteSegment = siteSegments.length > 1 ? siteSegments[1] : '';
      sameDomain = hostname.includes(siteConfig.domain);
      return sameDomain || path === siteSegment || siteConfig.site === '*';
    });
    if (sameDomain && matchedConfig) {
      matchedConfig = {
        ...matchedConfig,
        site: ''
      };
    }
    return matchedConfig ? this.checkDisabledLocales(matchedConfig) : undefined;
  }
  checkDisabledLocales(config) {
    const locale = this.localeService.getFromStorage();
    const language = locale?.language?.toLocaleLowerCase() ?? '';
    const country = locale?.country?.toLocaleLowerCase() ?? '';
    const currLocale = `${language}-${country}`;
    if (config.disable_locales?.includes(currLocale)) {
      return {
        ...config,
        display_global_navigation: 'false',
        display_global_footer: 'false'
      };
    }
    return config;
  }
  static {
    this.ɵfac = function TcmService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || TcmService)(i0.ɵɵinject(i1.AppEnvironmentService), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.LocaleService), i0.ɵɵinject(i4.SessionStorageService), i0.ɵɵinject('TridionBaseUrl'), i0.ɵɵinject(WINDOW));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: TcmService,
      factory: TcmService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TcmService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.AppEnvironmentService
  }, {
    type: i2.HttpClient
  }, {
    type: i3.LocaleService
  }, {
    type: i4.SessionStorageService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['TridionBaseUrl']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }], null);
})();

/*
 * Public API Surface of tcm-service
 */

/**
 * Generated bundle index. Do not edit.
 */

export { TCM_ENV_DEFAULTS, TcmService };
